/**
 * Page archetypes — whole screens, already proportioned and already dense.
 *
 * The primitives in `primitives.tsx` are parts; these are the five shapes
 * almost every screen in an app actually is. Reach for an archetype first and
 * a primitive only when the screen is genuinely something else, so a new
 * screen starts elegant instead of being made elegant afterwards.
 *
 * Each one bakes in the rules that are otherwise remembered: the four data
 * states, the measure, the page width for its purpose, a non-modal detail
 * panel, 44px targets. See docs/LAYOUT.md and docs/RULES-OF-THUMB.md.
 */
import type { LucideIcon } from 'lucide-react'
import { type ReactNode, useId } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { useIsMobile } from '@/lib/hooks'
import { cn } from '@/lib/utils'
import { ListCard, Page, PageHeader, Section, StatGrid } from './primitives.tsx'

/* ─────────────────────────── shared state slots ─────────────────────────── */

export interface ScreenState {
  loading?: boolean
  error?: boolean
  onRetry?: () => void
  /** True when a search or filter is on and produced nothing. */
  filtered?: boolean
  onClearFilter?: () => void
}

export interface EmptyCopy {
  icon: LucideIcon
  title: string
  description: string
  action?: { label: string; onClick: () => void }
}

/** Rows that mirror the geometry of what is coming, not a spinner. */
export function RowSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <ListCard>
      {Array.from({ length: rows }, (_, i) => `skeleton-${i}`).map((key) => (
        <div key={key} className="flex min-h-16 items-center gap-4 px-6 py-4">
          <Skeleton className="size-10 shrink-0 rounded-full" />
          <div className="flex flex-1 flex-col gap-2">
            <Skeleton className="h-4 w-2/5" />
            <Skeleton className="h-3 w-1/4" />
          </div>
          <Skeleton className="h-6 w-16 rounded-full" />
        </div>
      ))}
    </ListCard>
  )
}

function CenteredNotice({
  icon: Icon,
  title,
  description,
  action,
}: {
  icon?: LucideIcon
  title: string
  description?: string
  action?: { label: string; onClick: () => void }
}) {
  return (
    <div className="flex flex-col items-center gap-4 rounded-lg border border-dashed px-6 py-14 text-center">
      {Icon ? (
        <div className="flex size-12 items-center justify-center rounded-full bg-muted">
          <Icon className="size-6 text-muted-foreground" aria-hidden="true" />
        </div>
      ) : null}
      <div className="flex flex-col gap-1">
        <p className="text-base font-semibold">{title}</p>
        {description ? (
          <p className="max-w-measure text-sm text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {action ? (
        <Button onClick={action.onClick} size="sm">
          {action.label}
        </Button>
      ) : null}
    </div>
  )
}

/**
 * Renders the right one of loading / error / empty / filter-empty / content —
 * so no screen can quietly ship only the happy path.
 */
export function StateSlot({
  state,
  empty,
  isEmpty,
  skeleton,
  children,
}: {
  state: ScreenState
  empty: EmptyCopy
  isEmpty: boolean
  skeleton?: ReactNode
  children: ReactNode
}) {
  if (state.loading) return <>{skeleton ?? <RowSkeleton />}</>

  if (state.error) {
    return (
      <CenteredNotice
        title="That didn't load."
        description="Something went wrong fetching this. Your work is safe."
        action={state.onRetry ? { label: 'Try again', onClick: state.onRetry } : undefined}
      />
    )
  }

  if (isEmpty && state.filtered) {
    return (
      <CenteredNotice
        title="Nothing matches"
        description="No results for the current search or filters."
        action={
          state.onClearFilter ? { label: 'Clear filters', onClick: state.onClearFilter } : undefined
        }
      />
    )
  }

  if (isEmpty) {
    return (
      <CenteredNotice
        icon={empty.icon}
        title={empty.title}
        description={empty.description}
        action={empty.action}
      />
    )
  }

  return <>{children}</>
}

function PrimaryButton({ action }: { action: PrimaryAction }) {
  const Icon = action.icon
  return (
    <Button onClick={action.onClick}>
      {Icon ? <Icon aria-hidden="true" /> : null}
      {action.label}
    </Button>
  )
}

/* ───────────────────────────── 1 · List screen ──────────────────────────── */

/** The one thing someone came here to do. */
export interface PrimaryAction {
  label: string
  onClick: () => void
  icon?: LucideIcon
}

export interface ListScreenProps {
  /** The page's name — never shown (the header says it), only to name the search. */
  title: string
  primaryAction?: PrimaryAction
  search?: { value: string; onChange: (value: string) => void; placeholder?: string }
  toolbar?: ReactNode
  state: ScreenState
  empty: EmptyCopy
  isEmpty: boolean
  /** Usually ListRows inside a ListCard — pass the rows, the card is provided. */
  children: ReactNode
  /**
   * "Load more", under the rows. Lists are paged (CLAUDE.md "Scale"), so a
   * screen says when there is another page rather than pretending this is all.
   */
  more?: { hasMore: boolean; loading: boolean; onLoadMore: () => void }
  /** Opens as a non-modal side panel on desktop, a bottom sheet on a phone. */
  detail?: DetailPanelProps
}

export function ListScreen({
  title,
  primaryAction,
  search,
  toolbar,
  state,
  empty,
  isEmpty,
  children,
  more,
  detail,
}: ListScreenProps) {
  const searchId = useId()

  return (
    <Page>
      <PageHeader actions={primaryAction ? <PrimaryButton action={primaryAction} /> : undefined} />

      {search || toolbar ? (
        <div className="flex flex-wrap items-center gap-3">
          {search ? (
            <Input
              id={searchId}
              type="search"
              value={search.value}
              onChange={(e) => search.onChange(e.target.value)}
              placeholder={search.placeholder ?? `Search ${title.toLowerCase()}…`}
              aria-label={`Search ${title.toLowerCase()}`}
              // Full width on a phone, with the toolbar wrapping below it — as a
              // shrinking flex item it was squeezed to "Search proje".
              className="basis-full sm:max-w-sm sm:flex-1 sm:basis-auto"
            />
          ) : null}
          {toolbar}
        </div>
      ) : null}

      <StateSlot state={state} empty={empty} isEmpty={isEmpty}>
        <ListCard>{children}</ListCard>
        {more?.hasMore ? (
          <div className="flex justify-center pt-4">
            <Button variant="outline" onClick={more.onLoadMore} disabled={more.loading}>
              {more.loading ? 'Loading…' : 'Load more'}
            </Button>
          </div>
        ) : null}
      </StateSlot>

      {detail ? <DetailPanel {...detail} /> : null}
    </Page>
  )
}

/* ──────────────────────────── 2 · Detail panel ──────────────────────────── */

export interface DetailPanelProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  /** Shown under the title — a status, an owner, a date. */
  subtitle?: ReactNode
  actions?: ReactNode
  children: ReactNode
}

/**
 * Detail about one record, beside the list. Non-modal on desktop so the list
 * stays usable and you can click straight to the next record (Material 3 side
 * sheets, Carbon's right panel); a modal bottom sheet on a phone, where there
 * is nothing behind it anyway. A third of the width, every time.
 */
export function DetailPanel({
  open,
  onOpenChange,
  title,
  subtitle,
  actions,
  children,
}: DetailPanelProps) {
  const isMobile = useIsMobile()

  return (
    // `modal` goes on `Sheet` only — `SheetContent` reads it back out of
    // context (src/components/ui/sheet.tsx), so it can't drift out of sync
    // with what actually stops Base UI locking page scroll.
    <Sheet open={open} onOpenChange={onOpenChange} modal={isMobile}>
      <SheetContent
        side={isMobile ? 'bottom' : 'right'}
        className={cn('overflow-y-auto', isMobile && 'max-h-[85vh]')}
        style={isMobile ? undefined : { width: 'min(33vw, 520px)', maxWidth: 'none' }}
      >
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          {subtitle ? <div className="text-sm text-muted-foreground">{subtitle}</div> : null}
        </SheetHeader>

        <div className="flex flex-col gap-6 px-6 pb-6">{children}</div>

        {actions ? (
          <div className="mt-auto flex flex-wrap gap-3 border-t px-6 py-4">{actions}</div>
        ) : null}
      </SheetContent>
    </Sheet>
  )
}

/* ───────────────────────────── 3 · Dashboard ────────────────────────────── */

export interface DashboardScreenProps {
  primaryAction?: PrimaryAction
  /** StatCards. Aim for four — one or two stretched across a page reads as unfinished. */
  stats: ReactNode
  state: ScreenState
  children?: ReactNode
}

export function DashboardScreen({ primaryAction, stats, state, children }: DashboardScreenProps) {
  return (
    <Page>
      <PageHeader actions={primaryAction ? <PrimaryButton action={primaryAction} /> : undefined} />

      {state.loading ? (
        <StatGrid>
          {Array.from({ length: 4 }, (_, i) => `stat-${i}`).map((key) => (
            <div
              key={key}
              className="flex flex-col gap-2 rounded-lg border bg-card shadow-resting px-6 py-5"
            >
              <Skeleton className="h-4 w-2/5" />
              <Skeleton className="h-9 w-1/3" />
              <Skeleton className="h-4 w-3/5" />
            </div>
          ))}
        </StatGrid>
      ) : (
        <StatGrid>{stats}</StatGrid>
      )}

      {children}
    </Page>
  )
}

/* ───────────────────────────── 4 · Settings ─────────────────────────────── */

export interface SettingsScreenProps {
  /** Groups of SettingRows. Each group becomes one bordered list. */
  groups: { title?: string; rows: ReactNode }[]
}

/**
 * Choices and forms cap at 820px — a row holding one control has no business
 * being a screen wide.
 */
export function SettingsScreen({ groups }: SettingsScreenProps) {
  return (
    <Page width="settings">
      {groups.map((group, index) => (
        <Section key={group.title ?? `group-${index}`} title={group.title}>
          <ListCard>{group.rows}</ListCard>
        </Section>
      ))}
    </Page>
  )
}

/* ──────────────────────── 5 · Split (inbox / chat) ──────────────────────── */

export interface SplitScreenProps {
  /** The page's name, on the phone's back button. */
  title: string
  /** The left-hand list: conversations, threads, records. */
  list: ReactNode
  /** The open item. When nothing is selected, `placeholder` shows instead. */
  detail?: ReactNode
  placeholder?: { icon?: LucideIcon; title: string; description?: string }
  /** True when an item is open — drives the phone push-screen behaviour. */
  hasSelection: boolean
  onBack?: () => void
  listWidth?: number
}

/**
 * Master–detail: a list beside the open item on desktop, and on a phone the
 * list IS the screen until you pick something, which pushes the detail in.
 * The shape behind every inbox, chat and thread view.
 */
export function SplitScreen({
  title,
  list,
  detail,
  placeholder,
  hasSelection,
  onBack,
  listWidth = 320,
}: SplitScreenProps) {
  const isMobile = useIsMobile()

  if (isMobile) {
    return hasSelection ? (
      <div className="flex min-h-0 flex-1 flex-col gap-4">
        {onBack ? (
          <Button variant="ghost" size="sm" onClick={onBack} className="self-start">
            ← {title}
          </Button>
        ) : null}
        <div className="min-h-0 flex-1">{detail}</div>
      </div>
    ) : (
      <Page>
        <ListCard>{list}</ListCard>
      </Page>
    )
  }

  return (
    <div className="flex min-h-0 flex-1 gap-6">
      <div
        className="flex min-h-0 shrink-0 flex-col overflow-hidden rounded-lg border bg-card shadow-resting"
        style={{ width: listWidth }}
      >
        <div className="min-h-0 flex-1 divide-y overflow-y-auto">{list}</div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border bg-card shadow-resting">
        {hasSelection ? (
          detail
        ) : (
          <div className="flex flex-1 items-center justify-center p-6">
            <CenteredNotice
              icon={placeholder?.icon}
              title={placeholder?.title ?? 'Nothing selected'}
              description={placeholder?.description}
            />
          </div>
        )}
      </div>
    </div>
  )
}
